import { installMultiplayerPlayerSnakesTestApi } from '../devtools/MultiplayerPlayerSnakesTestApi.js';
import { MultiplayerPlayerSnakeSystem } from '../engine/multiplayer/MultiplayerPlayerSnakeSystem.js';

export const MULTIPLAYER_PLAYER_SNAKES_INTEGRATION_VERSION = 1;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

function assertMethod(value, method, name) {
  if (!value || typeof value[method] !== 'function') {
    throw new TypeError(`${name} must provide ${method}()`);
  }
}

function playerColor(playerId) {
  let hash = 0;
  for (let index = 0; index < playerId.length; index += 1) {
    hash = Math.imul(hash ^ playerId.charCodeAt(index), 0x45d9f3b);
  }
  const hue = Math.abs(hash) % 360;
  return {
    body: `hsl(${hue} 42% 48%)`,
    head: `hsl(${hue} 48% 57%)`,
    text: `hsl(${hue} 55% 78%)`,
  };
}

export class MultiplayerPlayerSnakesIntegration {
  constructor({
    app,
    windowRef = globalThis.window,
    documentRef = globalThis.document,
  }) {
    if (!app || typeof app !== 'object') throw new TypeError('app must be an object');
    assertMethod(app.bus, 'on', 'app.bus');
    assertMethod(app.bus, 'emit', 'app.bus');
    assertMethod(app.entityWorld, 'snapshot', 'app.entityWorld');
    assertMethod(app.traversalWorld, 'snapshot', 'app.traversalWorld');
    assertMethod(app.snakeState, 'snapshot', 'app.snakeState');
    assertMethod(app.multiplayerState, 'getStatus', 'app.multiplayerState');
    assertMethod(app.rendering, 'captureRenderState', 'app.rendering');
    assertMethod(app.rendering, 'setCamera', 'app.rendering');
    if (!documentRef?.createElement) throw new TypeError('documentRef must provide createElement()');
    this.app = app;
    this.windowRef = windowRef;
    this.documentRef = documentRef;
    this.system = new MultiplayerPlayerSnakeSystem({
      entityWorld: app.entityWorld,
      traversalWorld: app.traversalWorld,
      localSnakeState: app.snakeState,
      multiplayerState: app.multiplayerState,
    });
    this.canvas = null;
    this.context = null;
    this.host = null;
    this.installed = false;
    this.rafId = null;
    this.frameCount = 0;
    this.lastChecksum = null;
    this.lastTick = null;
    this.lastError = null;
    this.width = 0;
    this.height = 0;
    this.devicePixelRatio = 1;
    this.detachMultiplayerStep = null;
    this.detachMultiplayerChange = null;
    this.detachSimulationReset = null;
    this.detachSaveLoad = null;
    this.onTestLabClick = (event) => this.#handleTestLabClick(event);
  }

  install() {
    if (this.installed) return this.getStatus();
    this.host = this.app.rendering.engine2Host
      ?? this.app.mount?.querySelector?.('[data-engine2-host]');
    if (!this.host) throw new Error('Engine 2 rendering host is unavailable');
    if (!this.host.style.position) this.host.style.position = 'relative';
    this.canvas = this.documentRef.createElement('canvas');
    this.canvas.className = 'multiplayer-player-snakes-overlay';
    this.canvas.dataset.multiplayerPlayerSnakes = '';
    Object.assign(this.canvas.style, {
      position: 'absolute',
      inset: '0',
      width: '100%',
      height: '100%',
      pointerEvents: 'none',
      zIndex: '3',
    });
    this.context = this.canvas.getContext?.('2d') ?? null;
    if (!this.context) throw new Error('Canvas 2D rendering is unavailable for multiplayer snakes');
    this.host.append(this.canvas);

    this.app.multiplayerPlayerSnakeSystem = this.system;
    this.app.multiplayerPlayerSnakes = this;
    this.app.runtimeState.multiplayerPlayerSnakes = this.system.getStatus();
    this.app.testApi = installMultiplayerPlayerSnakesTestApi({
      baseApi: this.app.testApi,
      system: this.system,
      integration: this,
      bus: this.app.bus,
      simulationSnapshots: this.app.simulationSnapshots,
      windowRef: this.windowRef,
    });
    if (this.app.testLab) this.app.testLab.testApi = this.app.testApi;

    this.detachMultiplayerStep = this.app.bus.on('multiplayer:step', (event) => this.#handleStep(event));
    this.detachMultiplayerChange = this.app.bus.on('multiplayer-state:changed', () => this.#handleStateChange());
    this.detachSimulationReset = this.app.bus.on('simulation:reset', () => this.reset());
    this.detachSaveLoad = this.app.bus.on('save:loaded', () => this.reset());
    this.#installTestLabControls();
    this.installed = true;
    this.#syncRuntime();
    this.#scheduleFrame();
    return this.getStatus();
  }

  destroy() {
    this.windowRef?.cancelAnimationFrame?.(this.rafId);
    this.rafId = null;
    this.detachMultiplayerStep?.();
    this.detachMultiplayerChange?.();
    this.detachSimulationReset?.();
    this.detachSaveLoad?.();
    this.detachMultiplayerStep = null;
    this.detachMultiplayerChange = null;
    this.detachSimulationReset = null;
    this.detachSaveLoad = null;
    this.app.testLab?.root?.removeEventListener('click', this.onTestLabClick);
    this.system.reset({ preserveLocalSnake: true });
    this.canvas?.remove?.();
    this.canvas = null;
    this.context = null;
    this.host = null;
    this.installed = false;
    this.#syncRuntime();
    return this.getStatus();
  }

  syncNow() {
    const status = this.app.multiplayerState.getStatus();
    if (status.active && status.status === 'running') {
      this.system.sync({ localPlayerId: this.app.multiplayer.localPlayerId });
    } else {
      this.system.reset({ preserveLocalSnake: true });
    }
    this.#syncRuntime();
    return this.system.getStatus();
  }

  reset() {
    const status = this.system.reset({ preserveLocalSnake: true });
    this.#clear();
    this.#syncRuntime();
    return status;
  }

  focusPlayer(playerId) {
    const snake = this.system.getPlayerSnake(playerId);
    if (!snake) throw new RangeError(`No multiplayer snake exists for player: ${playerId}`);
    const camera = this.app.rendering.setCamera({
      followSnake: false,
      followHeading: false,
      target: snake.position,
      heading: snake.heading,
    });
    this.renderNow();
    return camera;
  }

  renderNow() {
    if (!this.canvas || !this.context) return this.getStatus();
    const renderState = this.app.rendering.captureRenderState();
    this.#resize();
    this.#clear();
    const remote = this.system.listSnakes().filter((record) => !record.local);
    if (this.app.rendering.getStatus().mode !== 'engine2') {
      this.canvas.hidden = true;
      return this.getStatus();
    }
    this.canvas.hidden = false;
    const project = this.#createProjection(renderState.camera);
    for (const record of remote) this.#drawSnake(record, renderState.camera, project);
    this.frameCount += 1;
    this.lastChecksum = this.system.snapshot().checksum;
    this.lastTick = this.system.getStatus().tick;
    this.lastError = null;
    this.canvas.dataset.snakeCount = String(remote.length);
    this.canvas.dataset.totalSnakeCount = String(this.system.getStatus().snakeCount);
    this.canvas.dataset.localPlayerId = this.system.getStatus().localPlayerId ?? '';
    this.canvas.dataset.checksum = this.lastChecksum;
    this.canvas.dataset.tick = this.lastTick === null ? '' : String(this.lastTick);
    return this.getStatus();
  }

  getStatus() {
    return Object.freeze({
      version: MULTIPLAYER_PLAYER_SNAKES_INTEGRATION_VERSION,
      installed: this.installed,
      frameCount: this.frameCount,
      lastChecksum: this.lastChecksum,
      lastTick: this.lastTick,
      lastError: this.lastError,
      width: this.width,
      height: this.height,
      devicePixelRatio: this.devicePixelRatio,
      system: this.system.getStatus(),
    });
  }

  #handleStep(event) {
    const status = this.app.multiplayerState.getStatus();
    if (!status.active || status.status !== 'running') return;
    try {
      this.system.step({
        tick: event.simulationTick,
        simulationTime: status.simulationTime,
        stepSeconds: status.stepSeconds,
        localPlayerId: this.app.multiplayer.localPlayerId,
      });
      this.lastError = null;
      this.#syncRuntime();
      this.app.bus.emit('multiplayer-player-snakes:step', this.system.lastStep);
      if (this.app.rendering.getStatus().mode === 'engine2') this.renderNow();
    } catch (error) {
      this.lastError = error instanceof Error ? error.message : String(error);
      this.#syncRuntime();
      this.app.bus.emit('multiplayer-player-snakes:error', {
        tick: event.simulationTick,
        error: this.lastError,
      });
    }
  }

  #handleStateChange() {
    try {
      this.syncNow();
      if (this.app.rendering.getStatus().mode === 'engine2') this.renderNow();
    } catch (error) {
      this.lastError = error instanceof Error ? error.message : String(error);
      this.#syncRuntime();
    }
  }

  #resize() {
    const rect = this.host?.getBoundingClientRect?.() ?? {};
    const width = Math.max(1, Math.round(rect.width || this.host?.clientWidth || this.windowRef?.innerWidth || 640));
    const height = Math.max(1, Math.round(rect.height || this.host?.clientHeight || this.windowRef?.innerHeight || 360));
    const dpr = clamp(Number.isFinite(this.windowRef?.devicePixelRatio)
      ? this.windowRef.devicePixelRatio
      : 1, 1, 2);
    const pixelWidth = Math.round(width * dpr);
    const pixelHeight = Math.round(height * dpr);
    if (this.canvas.width !== pixelWidth) this.canvas.width = pixelWidth;
    if (this.canvas.height !== pixelHeight) this.canvas.height = pixelHeight;
    this.width = width;
    this.height = height;
    this.devicePixelRatio = dpr;
    this.context.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  #clear() {
    if (!this.context || !this.canvas) return;
    this.context.setTransform(1, 0, 0, 1, 0, 0);
    this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.context.setTransform(this.devicePixelRatio, 0, 0, this.devicePixelRatio, 0, 0);
  }

  #createProjection(camera) {
    const scale = (Math.min(this.width, this.height) * 0.5) / (camera.viewRadius / camera.zoom);
    const forwardX = Math.sin(camera.heading);
    const forwardZ = Math.cos(camera.heading);
    const rightX = Math.cos(camera.heading);
    const rightZ = -Math.sin(camera.heading);
    return (position) => {
      const dx = position.x - camera.target.x;
      const dz = position.z - camera.target.z;
      return {
        x: (this.width * 0.5) + (((dx * rightX) + (dz * rightZ)) * scale),
        y: (this.height * 0.56)
          - (((dx * forwardX) + (dz * forwardZ)) * scale)
          - ((position.y - camera.target.y) * scale),
        scale,
      };
    };
  }

  #drawSnake(record, camera, project) {
    const snake = record.snake;
    const points = snake.bodySegments.map(project);
    if (points.every((point) => point.x < -80 || point.x > this.width + 80
      || point.y < -80 || point.y > this.height + 80)) return;
    const colors = playerColor(record.playerId);
    const baseRadius = clamp(points[0].scale * (0.075 + (snake.mass * 0.08)), 3.5, 24);
    const context = this.context;
    context.save();
    context.globalAlpha = record.playerStatus === 'disconnected'
      ? 0.38
      : snake.lifecycle === 'dead' ? 0.5 : 0.92;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.strokeStyle = colors.body;
    context.lineWidth = baseRadius * 1.55;
    context.beginPath();
    const tail = points[points.length - 1];
    context.moveTo(tail.x, tail.y);
    for (let index = points.length - 2; index >= 0; index -= 1) {
      context.lineTo(points[index].x, points[index].y);
    }
    context.stroke();
    for (let index = points.length - 1; index >= 0; index -= 1) {
      const progress = 1 - (index / Math.max(1, points.length - 1));
      context.fillStyle = colors.body;
      context.beginPath();
      context.arc(points[index].x, points[index].y, baseRadius * (0.45 + (progress * 0.55)), 0, Math.PI * 2);
      context.fill();
    }
    const head = points[0];
    context.translate(head.x, head.y);
    context.rotate(snake.heading - camera.heading);
    context.fillStyle = colors.head;
    context.beginPath();
    context.ellipse(0, -baseRadius * 0.12, baseRadius * 0.95, baseRadius * 1.2, 0, 0, Math.PI * 2);
    context.fill();
    context.fillStyle = '#09110b';
    context.beginPath();
    context.arc(-baseRadius * 0.42, -baseRadius * 0.62, Math.max(1.1, baseRadius * 0.12), 0, Math.PI * 2);
    context.arc(baseRadius * 0.42, -baseRadius * 0.62, Math.max(1.1, baseRadius * 0.12), 0, Math.PI * 2);
    context.fill();
    context.restore();

    context.save();
    context.fillStyle = colors.text;
    context.font = '600 11px ui-sans-serif, system-ui';
    context.textAlign = 'center';
    const suffix = record.playerStatus === 'disconnected' ? ' · reconnecting' : '';
    context.fillText(`${record.displayName}${suffix}`, head.x, head.y - baseRadius - 10);
    context.restore();
  }

  #scheduleFrame() {
    if (!this.installed || typeof this.windowRef?.requestAnimationFrame !== 'function') return;
    this.rafId = this.windowRef.requestAnimationFrame(() => {
      try {
        if (this.app.rendering.getStatus().mode === 'engine2') this.renderNow();
      } catch (error) {
        this.lastError = error instanceof Error ? error.message : String(error);
        this.#syncRuntime();
      }
      this.#scheduleFrame();
    });
  }

  #syncRuntime() {
    if (!this.app.runtimeState) return;
    this.app.runtimeState.multiplayerPlayerSnakes = this.system.getStatus();
    const node = this.app.mount?.querySelector?.('[data-status="multiplayer-snakes"]');
    if (node) {
      const status = this.system.getStatus();
      node.textContent = status.active
        ? `${status.snakeCount} snakes · Tick ${status.tick ?? '—'}`
        : 'Inactive';
    }
  }

  #installTestLabControls() {
    const root = this.app.testLab?.root;
    const toolbar = root?.querySelector?.('.test-lab__toolbar');
    if (root && toolbar && !toolbar.querySelector('[data-action="multiplayer-snakes"]')) {
      const button = this.documentRef.createElement('button');
      button.type = 'button';
      button.dataset.action = 'multiplayer-snakes';
      button.textContent = 'Player Snakes';
      toolbar.append(button);
      root.addEventListener('click', this.onTestLabClick);
    }
    const statusGrid = this.app.mount?.querySelector?.('.status-grid');
    if (statusGrid && !statusGrid.querySelector('[data-multiplayer-snakes-status-row]')) {
      const row = this.documentRef.createElement('div');
      row.dataset.multiplayerSnakesStatusRow = '';
      row.innerHTML = '<dt>Player Snakes</dt><dd data-status="multiplayer-snakes">Inactive</dd>';
      statusGrid.append(row);
    }
  }

  #handleTestLabClick(event) {
    if (event.target.closest('[data-action]')?.dataset.action !== 'multiplayer-snakes') return;
    let result;
    try {
      this.syncNow();
      result = this.system.getDiagnostics();
    } catch (error) {
      result = { ok: false, error: error instanceof Error ? error.message : String(error) };
    }
    const output = this.app.testLab?.root?.querySelector?.('.test-lab__output');
    if (output) output.textContent = JSON.stringify(result, null, 2);
  }
}
